import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { parseRoles, sectionsFor, type MenuItem } from '../menu.js';
import { SETTINGS_SECTION_TITLE, canSeeSettingsItem } from '../lib/settingsAccess.js';
import { SectionTitle, sectionBrand } from '../components/SectionTitle.js';

/**
 * 로그인 후 첫 화면. 원본 home.php 처럼 카드 섹션에 모든 메뉴를 펼쳐 보여준다.
 *
 * 원본은 메뉴를 이 화면에서만 보여줬다. 쓰던 사람이 "왼쪽 위 두 번째" 처럼
 * 위치로 기억하고 있어서, 섹션과 항목 순서를 원본 그대로 둔다.
 */

function Item({ item }: { item: MenuItem }) {
  if (item.to) {
    return (
      <Link className="menu-item menu-item--ready" to={item.to}>
        {item.name}
      </Link>
    );
  }

  if (item.disabled) {
    return (
      <span className="menu-item menu-item--off" title={item.disabled}>
        {item.name}
        <span className="menu-item__tag">{item.disabled}</span>
      </span>
    );
  }

  if (item.external) {
    return (
      <a className="menu-item" href={item.external} target="_blank" rel="noreferrer">
        {item.name}
        <span className="menu-item__tag">↗</span>
      </a>
    );
  }

  return (
    <a className="menu-item" href={item.legacy} title="아직 이관되지 않은 화면입니다">
      {item.name}
      <span className="menu-item__tag menu-item__tag--php">PHP</span>
    </a>
  );
}

export function HomePage() {
  const { user } = useAuth();
  // '설정'은 화면 단위로 거른다 - 관리자 계정은 최고 관리자 권한이 있으면
  // 누구나, 화이트 IP 는 xadmin 한 명만 본다(settingsAccess.ts 참고). 한때
  // 이 섹션을 sec:settings 로 개별 부여할 수 있었지만, 최고 관리자가 아닌
  // 계정도 다른 관리자 계정·화이트 IP 를 건드릴 수 있게 돼 2026-09-03 에
  // 없앴다 (AdminAccountsPage.tsx, server 의 requireRole('admin') 단독 가드 참고).
  const roles = parseRoles(user?.authtype);
  const sections = sectionsFor(user?.authtype)
    .map((s) =>
      s.title === SETTINGS_SECTION_TITLE
        ? { ...s, items: s.items.filter((i) => canSeeSettingsItem(roles, user?.userid, i.to)) }
        : s,
    )
    .filter((s) => s.title !== SETTINGS_SECTION_TITLE || s.items.length > 0);

  const total = sections.reduce((n, s) => n + s.items.length, 0);
  const ready = sections.reduce((n, s) => n + s.items.filter((i) => i.to).length, 0);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">{user?.name ?? user?.userid}님, 안녕하세요</h1>
          <p className="page-desc">
            메뉴 {total}개 중 {ready}개가 새 화면으로 이관됐습니다. 나머지는 기존 화면으로
            연결됩니다.
          </p>
        </div>
      </header>

      {sections.length === 0 ? (
        <p className="page-desc">이 계정으로 볼 수 있는 메뉴가 없습니다.</p>
      ) : (
        <div className="menu-grid">
          {sections.map((section) => {
            const brand = sectionBrand(section);
            return (
              <section className={brand ? `menu-card menu-card--${brand}` : 'menu-card'} key={section.title}>
                <h2 className="menu-card__title">
                  <SectionTitle section={section} />
                </h2>
                <div className="menu-card__items">
                  {section.items.map((item) => (
                    <Item key={item.name} item={item} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}
